// "Show it": the coach opens one screen during a live call and the call shrinks
// to the floating card. A deliberate tool, separate from ordinary actions.

export const COACH_SHOW_SURFACES = [
  'goal',
  'habit',
  'today',
  'stats',
  'everyday',
  'templates',
] as const

export type CoachShowSurface = (typeof COACH_SHOW_SURFACES)[number]

export type CoachShowRequest = {
  surface: CoachShowSurface
  goalId?: string
  section?: string
  habitId?: string
  taskIds?: string[]
  tab?: 'habits' | 'tasks' | 'events' | 'goals'
  templateId?: string
}

const TABS = new Set(['habits', 'tasks', 'events', 'goals'])

const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

export function parseCoachShow(
  args: Record<string, unknown>,
): CoachShowRequest | { error: string } {
  const surface = text(args.surface)
  if (!surface || !COACH_SHOW_SURFACES.includes(surface as CoachShowSurface))
    return { error: 'surface must be goal, habit, today, stats, everyday or templates' }
  const tabRaw = text(args.tab)
  const tab = tabRaw && TABS.has(tabRaw) ? (tabRaw as CoachShowRequest['tab']) : undefined
  const taskIds = Array.isArray(args.taskIds)
    ? args.taskIds
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
        .map((id) => id.trim())
        .slice(0, 12)
    : undefined
  const req: CoachShowRequest = {
    surface: surface as CoachShowSurface,
    ...(text(args.goalId) ? { goalId: text(args.goalId) } : {}),
    ...(text(args.section) ? { section: text(args.section) } : {}),
    ...(text(args.habitId) ? { habitId: text(args.habitId) } : {}),
    ...(taskIds && taskIds.length ? { taskIds } : {}),
    ...(tab ? { tab } : {}),
    ...(text(args.templateId) ? { templateId: text(args.templateId) } : {}),
  }
  if (req.surface === 'goal' && !req.goalId) return { error: 'goalId is required to open a goal' }
  if (req.surface === 'habit' && !req.habitId)
    return { error: 'habitId is required to open a habit' }
  return req
}

type GoalNode = {
  _id: string
  title: string
  targets?: { _id: string; title: string; tasks: { _id: string; title: string }[] }[]
}

// Title or id of a milestone, target or task inside a goal.
export function matchGoalSection(milestones: GoalNode[], section: string): string | undefined {
  const raw = section.trim()
  const q = raw.toLowerCase()
  if (!q) return undefined
  const nodes: { id: string; title: string }[] = []
  for (const ms of milestones) {
    nodes.push({ id: ms._id, title: ms.title })
    for (const tg of ms.targets ?? []) {
      nodes.push({ id: tg._id, title: tg.title })
      for (const tk of tg.tasks) nodes.push({ id: tk._id, title: tk.title })
    }
  }
  return (
    nodes.find((n) => n.id === raw)?.id ??
    nodes.find((n) => n.title.trim().toLowerCase() === q)?.id ??
    nodes.find((n) => n.title.toLowerCase().includes(q))?.id
  )
}

export const SHOW_WAIT_MS = 4000
// The spoken "let me pull that up" can arrive just after the tool call.
const SHOW_ARM_MS = 900

export type CoachShowResult = {
  opened: boolean
  where?: string
  reason?: string
  section?: string | null
}

// True once the user is quiet and the current line has finished, or the call ended.
export function coachShowQuiet(state: {
  live: boolean
  userTalking: boolean
  playing: boolean
  responsePending: boolean
  waitedMs: number
}): boolean {
  if (!state.live) return true
  if (state.userTalking || state.playing) return false
  if (state.responsePending && state.waitedMs < SHOW_ARM_MS) return false
  return true
}

export type CoachShowTarget =
  | { kind: 'goal'; goalId: string; focus?: string }
  | { kind: 'habit'; habitId: string }
  | { kind: 'today'; taskIds: string[] }
  | { kind: 'stats' }
  | { kind: 'everyday'; tab: 'habits' | 'tasks' | 'events' }
  | { kind: 'templates'; templateId?: string; tab?: 'goals' | 'habits' }

export function coachShowTarget(req: CoachShowRequest, sectionId?: string): CoachShowTarget {
  switch (req.surface) {
    case 'goal':
      return { kind: 'goal', goalId: req.goalId ?? '', ...(sectionId ? { focus: sectionId } : {}) }
    case 'habit':
      return { kind: 'habit', habitId: req.habitId ?? '' }
    case 'today':
      return { kind: 'today', taskIds: req.taskIds ?? [] }
    case 'stats':
      return { kind: 'stats' }
    case 'everyday':
      return {
        kind: 'everyday',
        tab: req.tab === 'tasks' || req.tab === 'events' ? req.tab : 'habits',
      }
    case 'templates':
      return {
        kind: 'templates',
        ...(req.templateId ? { templateId: req.templateId } : {}),
        ...(req.tab === 'goals' || req.tab === 'habits' ? { tab: req.tab } : {}),
      }
  }
}

export function waitUntil(pred: () => boolean, ms: number, step = 80): Promise<void> {
  const start = Date.now()
  return new Promise((resolve) => {
    const tick = () => {
      if (pred() || Date.now() - start >= ms) resolve()
      else setTimeout(tick, step)
    }
    tick()
  })
}

export const SHOW_IN_APP_TOOL = {
  type: 'function' as const,
  name: 'show_in_app',
  description:
    'Open one screen so the user can look at it, and shrink the live call to the floating card. Use ONLY when you deliberately want them to look at a specific thing, and ONLY after you have already said out loud that you are pulling it up. Never use it when you merely tick something off, add a task, or answer a question. Say the line first, then call this once.',
  parameters: {
    type: 'object' as const,
    properties: {
      surface: {
        type: 'string',
        enum: [...COACH_SHOW_SURFACES],
        description:
          'goal opens a goal. habit opens one habit. today opens Today. stats opens stats. everyday opens Habits, Tasks or Events. templates opens the template library.',
      },
      goalId: { type: 'string', description: 'For surface goal. Id from list_goals.' },
      section: {
        type: 'string',
        description:
          'For surface goal. Milestone, target or task to scroll to and highlight. A title such as "Payments", or a node id.',
      },
      habitId: { type: 'string', description: 'For surface habit. Id from list_habits.' },
      taskIds: {
        type: 'array',
        items: { type: 'string' },
        description: 'For surface today. Task ids to glow briefly, usually ones just marked done.',
      },
      tab: {
        type: 'string',
        enum: ['habits', 'tasks', 'events', 'goals'],
        description: 'everyday: habits, tasks or events. templates: goals or habits.',
      },
      templateId: { type: 'string', description: 'For surface templates, to open one template.' },
    },
    required: ['surface'] as string[],
  },
}

export const SHOW_TURNED_OFF = {
  opened: false,
  reason:
    'The user turned off letting you open things during calls. Tell them briefly and do not try again this call.',
}
